import { takeLatest, getContext, put, call } from 'redux-saga/effects';
import { ProjectId } from 'api/models';
import { TaskAPI } from 'api/tasks';
import { toastService } from 'components/molecules/toast';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { GanttType } from 'data/projects/gantt/ids';
import { loadProjectPlanTimelineHelpers } from 'data/projects/gantt/timeline-helpers-loader';
import { converToEntityId, PhaseEntityType } from 'data/projects/phase-entity';
import { getProjectTasksQueryKey, getProjectDetailQueryKey, getProjectDependencyMapKey } from 'queries/common';
import { getProjectBaselineQueryKey } from 'queries/project-baseline-mutation';
import { logger } from 'services';
import { createTaskSagaAction, bulkUpdateWbsSagaAction } from '../actions';
const createEmptyBaselines = (baselines)=>{
    return baselines.map((_baseline)=>({
            startDate: null,
            endDate: null
        }));
};
const calculateNewTaskWBS = (record, actualApiPosition, isPrivate)=>{
    const parentData = record.get('data');
    const parentWbs = parentData.wbs;
    const children = record.children || [];
    const realTasks = children === null || children === void 0 ? void 0 : children.filter((child)=>child.get('type') !== GanttType.AddNewTask);
    const isInsertBetween = actualApiPosition >= 0 && actualApiPosition < realTasks.length;
    if (isInsertBetween) {
        let lastSameTypeWbs = null;
        for(let i = actualApiPosition - 1; i >= 0; i -= 1){
            const task = realTasks[i];
            const taskData = task.get('data');
            if (taskData.private === isPrivate) {
                lastSameTypeWbs = taskData.wbs;
                break;
            }
        }
        if (lastSameTypeWbs) {
            const wbsParts = lastSameTypeWbs.split('.');
            const lastNumber = parseInt(wbsParts[wbsParts.length - 1], 10);
            wbsParts[wbsParts.length - 1] = (lastNumber + 1).toString();
            return wbsParts.join('.');
        }
        // inserting as the first shared task in between private tasks
        return `${parentWbs}.1`;
    }
    let tasks;
    if (isPrivate) {
        tasks = realTasks.filter((child)=>child.get('data').private);
    } else {
        tasks = realTasks.filter((child)=>!child.get('data').private);
    }
    const taskNumber = tasks.length + 1;
    return `${parentWbs}.${taskNumber}`;
};
function* createTaskSaga(action) {
    const { convertToGanttTask, convertToGanttStoreFormat } = yield call(loadProjectPlanTimelineHelpers);
    const ganttRef = yield getContext('ganttRef');
    if (!ganttRef) {
        return;
    }
    const { payload } = action;
    const { projectId, task, projectPhaseId, uniqueButtonId } = payload;
    const { taskStore } = ganttRef.instance;
    const isSubtask = task.parentTask !== undefined;
    let record;
    if (isSubtask) {
        record = taskStore.getById(`${GanttType.TaskBar}-${task.parentTask}`);
    } else {
        record = taskStore.getById(`${GanttType.ProjectPhaseBar}-${projectPhaseId}`);
    }
    if (!record) {
        return;
    }
    const queryClient = yield getQueryClientContext();
    let phaseIndex = 0;
    const previousProjectData = queryClient.getQueryData(getProjectDetailQueryKey(ProjectId(projectId)));
    if (previousProjectData === null || previousProjectData === void 0 ? void 0 : previousProjectData.projectPhases) {
        phaseIndex = previousProjectData.projectPhases.findIndex((phase)=>phase.projectPhaseId === projectPhaseId);
        if (phaseIndex === -1) {
            phaseIndex = 0;
        }
    }
    const children = record.children || [];
    let insertPosition = children.findIndex((child)=>{
        const childData = child.get('data');
        return child.get('type') === GanttType.AddNewTask && childData.id === uniqueButtonId;
    });
    if (insertPosition === -1) {
        insertPosition = children.length - 1;
    }
    let actualApiPosition = 0;
    for(let i = 0; i < insertPosition; i += 1){
        const child = children[i];
        if (child.get('type') !== GanttType.AddNewTask) {
            actualApiPosition += 1;
        }
    }
    const taskWithPosition = {
        ...task,
        position: actualApiPosition
    };
    try {
        const newTask = yield TaskAPI.createTask(projectId, taskWithPosition);
        const baselines = queryClient.getQueryData(getProjectBaselineQueryKey(projectId));
        const isPrivate = newTask.private || false;
        const recordData = record.get('data');
        const currentLevel = isSubtask ? (recordData.currentLevel || 1) + 1 : 1;
        const wbs = calculateNewTaskWBS(record, actualApiPosition, isPrivate);
        if (actualApiPosition >= 0 && actualApiPosition < children.filter((c)=>c.get('type') !== GanttType.AddNewTask).length) {
            if (isSubtask) {
                yield put(bulkUpdateWbsSagaAction({
                    isSubtask,
                    parentTaskId: task.parentTask,
                    actualApiPosition
                }));
            } else {
                yield put(bulkUpdateWbsSagaAction({
                    isSubtask,
                    parentTaskId: projectPhaseId,
                    actualApiPosition
                }));
            }
        }
        const ganttTask = {
            ...convertToGanttTask({
                ...newTask
            }),
            baselines: baselines ? createEmptyBaselines(baselines) : []
        };
        const formattedTask = convertToGanttStoreFormat(ganttTask, record.getStartDate(), phaseIndex, currentLevel, GanttType.TaskBar, wbs);
        record.insertChild(formattedTask, children[insertPosition]);
        const currentChildren = recordData.children || [];
        currentChildren.splice(insertPosition, 0, newTask.taskId);
        record.set('data', {
            ...recordData,
            children: currentChildren
        });
        if (previousProjectData) {
            const optimisticProjectData = {
                ...previousProjectData,
                projectPhases: previousProjectData.projectPhases.map((phase)=>{
                    if (phase.projectPhaseId === projectPhaseId) {
                        var _ref, _phase_totalTaskCount;
                        var _phase_aggregateProgressValues;
                        const phaseUpdate = {
                            ...phase,
                            aggregateProgressValues: {
                                ...phase.aggregateProgressValues,
                                taskCount: ((_ref = phase === null || phase === void 0 ? void 0 : (_phase_aggregateProgressValues = phase.aggregateProgressValues) === null || _phase_aggregateProgressValues === void 0 ? void 0 : _phase_aggregateProgressValues.taskCount) !== null && _ref !== void 0 ? _ref : 0) + 1
                            },
                            totalTaskCount: ((_phase_totalTaskCount = phase.totalTaskCount) !== null && _phase_totalTaskCount !== void 0 ? _phase_totalTaskCount : 0) + 1
                        };
                        if (!isSubtask) {
                            phaseUpdate.entityOrder = phase.entityOrder ? [
                                ...phase.entityOrder.slice(0, actualApiPosition),
                                converToEntityId(newTask.taskId, PhaseEntityType.Task),
                                ...phase.entityOrder.slice(actualApiPosition)
                            ] : [
                                converToEntityId(newTask.taskId, PhaseEntityType.Task)
                            ];
                        }
                        return phaseUpdate;
                    }
                    return phase;
                })
            };
            queryClient.setQueryData(getProjectDetailQueryKey(ProjectId(projectId)), optimisticProjectData);
        }
        const previousProjectTasksData = queryClient.getQueryData(getProjectTasksQueryKey(ProjectId(projectId)));
        const optimisticTasks = previousProjectTasksData ? [
            ...previousProjectTasksData.slice(0, actualApiPosition),
            newTask,
            ...previousProjectTasksData.slice(actualApiPosition)
        ] : [
            newTask
        ];
        if (newTask.parentTask) {
            if (optimisticTasks) {
                const updatedProjectTasks = optimisticTasks.map((_task)=>{
                    if (_task.taskId === newTask.parentTask) {
                        return {
                            ..._task,
                            subTasks: [
                                ..._task.subTasks || [],
                                newTask
                            ],
                            subTaskOrder: [
                                ..._task.subTaskOrder || [],
                                newTask.taskId
                            ]
                        };
                    }
                    return _task;
                });
                queryClient.setQueryData(getProjectTasksQueryKey(ProjectId(projectId)), updatedProjectTasks);
            }
        } else {
            queryClient.setQueryData(getProjectTasksQueryKey(ProjectId(projectId)), optimisticTasks);
        }
        queryClient.refetchQueries(getProjectDependencyMapKey(projectId));
    } catch (error) {
        logger.error('create task failed', {
            error
        });
        toastService({
            title: 'Task creation failed',
            message: ''
        }).error();
    }
}
export function* watchCreateTaskSaga() {
    yield takeLatest(createTaskSagaAction.type, createTaskSaga);
}
